import { PanelLeftClose, PanelLeftOpen, Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router";

import type { Corporation } from "@/api/corporations";
import { useAuth } from "@/auth/authState";
import {
  goItem,
  isItemActive,
  navGroups,
  pathMatchesItem,
  type NavItem,
  type NavSection,
} from "@/fixtures/navigation";
import { cx } from "@/lib/cx";

import { AtlasMark } from "./AtlasMark";
import { CorporationMark } from "./Header";
import "./Sidebar.css";

/**
 * De navy zijbalk van stijl A (ontwerp 28-10-2026): beeldmerk met
 * PortefeuilleAtlas en Q-Estate bovenaan, vier groepen met hun onderdelen, en
 * onderin Atlas-GO en Vraag Atlas AI.
 *
 * Elk onderdeel is één regel. De schermen ván een onderdeel staan in de
 * tabbalk boven de pagina (`SubNav`), niet meer in een openklappende lijst:
 * elf regels zijn in één oogopslag te scannen, veertig niet.
 *
 * De actieve markering is een oranje balk plus een lichter vlak; kleur alleen
 * is onvoldoende, en `aria-current` doet het werk voor wie het scherm niet ziet.
 *
 * Inklapbaar (ronde 3, 28-10-2026): ingeklapt staan alleen de iconen er, met de
 * naam als tooltip en voor de schermlezer. De knop staat onderin. Standaard dicht
 * bij een smal venster (`narrow`); de keuze wordt onthouden (`shellPreferences`).
 * ⌘K in de kop blijft de snelle weg naar een object.
 */
export function Sidebar({
  corporation,
  loading,
  collapsed = false,
  onToggle,
}: {
  /** Alleen voor het smalle scherm; `null` als er een keuzelijst in de header staat. */
  corporation: Corporation | null;
  loading: boolean;
  /** Alleen iconen, met de naam als tooltip. */
  collapsed?: boolean;
  /** Zonder deze functie staat er geen knop om in of uit te klappen. */
  onToggle?: () => void;
}) {
  const { pathname } = useLocation();
  const isAdmin = (useAuth().account?.roles ?? []).includes("admin");
  const ToggleIcon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <aside className={cx("atl-sidebar atl-on-navy", collapsed && "atl-sidebar--collapsed")}>
      <Link to="/" className="atl-sidebar__brand" aria-label="PortefeuilleAtlas, naar het dashboard">
        <AtlasMark className="atl-sidebar__mark" />
        <span className="atl-sidebar__product">
          <b>
            Portefeuille<em>Atlas</em>
          </b>
          <small>Q-Estate</small>
        </span>
        {/* Onder 1024 px wordt de zijbalk een strook en komt de header pas ná
            de navigatie; dan hoort het logo van de corporatie hier. */}
        {(corporation || loading) && (
          <span className="atl-sidebar__corporation">
            <CorporationMark corporation={corporation} loading={loading} />
          </span>
        )}
      </Link>

      <nav className="atl-sidebar__nav" aria-label="Hoofdnavigatie">
        {navGroups.map((group) => (
          <div className="atl-sidebar__group" key={group.title}>
            <p className="atl-sidebar__group-title">{group.title}</p>
            <ul>
              {group.sections.map((section) => (
                <li key={section.title}>
                  <SectionLink section={section} pathname={pathname} isAdmin={isAdmin} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="atl-sidebar__foot">
        <SidebarItem item={goItem} active={pathMatchesItem(goItem, pathname)} suffix="app" collapsed={collapsed} />
        <Link to="/atlas-ai" className="atl-sidebar__ai" title={collapsed ? "Vraag Atlas AI (⌘J)" : undefined}>
          <Sparkles size={17} strokeWidth={1.75} aria-hidden />
          <span className="atl-sidebar__label">Vraag Atlas AI</span>
          <kbd>⌘J</kbd>
        </Link>
        {onToggle && (
          <button
            type="button"
            className="atl-sidebar__toggle"
            onClick={onToggle}
            aria-expanded={!collapsed}
            title={collapsed ? "Zijbalk uitklappen" : "Zijbalk inklappen tot iconen"}
          >
            <ToggleIcon size={17} strokeWidth={1.75} aria-hidden />
            <span className="atl-sidebar__label">{collapsed ? "Uitklappen" : "Inklappen"}</span>
          </button>
        )}
      </div>
    </aside>
  );
}

/** Het eerste scherm van een onderdeel dat de gebruiker mag zien, bij voorkeur een gebouwd. */
function landingItem(section: NavSection, isAdmin: boolean): NavItem {
  const visible = section.items.filter((item) => isAdmin || !item.adminOnly);
  return visible.find((item) => item.built !== false) ?? visible[0] ?? section.items[0];
}

function SectionLink({ section, pathname, isAdmin, collapsed }: {
  section: NavSection; pathname: string; isAdmin: boolean; collapsed: boolean;
}) {
  const { hash } = useLocation();
  const active = section.items.some((item) => isItemActive(item, pathname, hash) || pathMatchesItem(item, pathname));
  const target = landingItem(section, isAdmin);
  return (
    <SidebarItem
      item={{ ...target, label: section.title, icon: section.icon }}
      active={active}
      collapsed={collapsed}
    />
  );
}

function SidebarItem({ item, active, suffix, collapsed = false }: {
  item: NavItem; active: boolean; suffix?: string; collapsed?: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className={`atl-sidebar__link${active ? " active" : ""}`}
      aria-current={active ? "page" : undefined}
      // Ingeklapt is de naam alleen nog tooltip; voor de schermlezer blijft hij de linktekst.
      title={collapsed ? item.label : undefined}
    >
      <Icon size={18} strokeWidth={1.75} aria-hidden />
      <span className="atl-sidebar__label">{item.label}</span>
      {suffix && <span className="atl-sidebar__suffix">{suffix}</span>}
    </Link>
  );
}
